import gql from "graphql-tag";
import { getHistoricalData } from "@/_core/data/api_calls/candle_data/getHistoricalData";
import { formatDate_YMDHMS, roundDateToFloor } from "@/_core/_helpers/_helpers";
import { apiService } from "@/_services";

/**
 * Retrieves the latest spot currency data with forward valuation.
 * Attempts to fetch the data from Redis if enabled; otherwise, falls back to an API request.
 *
 * @param {object} _this - The context object containing necessary data.
 * @param {number|null} [pk=null] - The primary key for the currency (optional).
 * @returns {Promise<object>} A promise resolving to the spot currency data.
 */
export function getSpotCurrency(_this, pk = null) {
  let fast = true; // Flag to determine whether to use Redis for faster retrieval.
  if (fast) {
    // GraphQL query to retrieve cached data from Redis.
    const paramsRedis = gql`
      query($key: String!) {
        getRedisData(key: $key)
      }
    `;
    let keySpot = ""; // Variable to store the currency identifier.
    // Determine the correct currency key based on available context data.
    if (_this.currencyDefs) {
      keySpot = _this.currencyDefs.currency_name;
    } else if (_this.currencyString) {
      keySpot = _this.currencyString;
    } else if (
      _this.currencySpecs &&
      _this[_this.fieldA] &&
      _this.currencySpecs[_this[_this.fieldA][0]]
    ) {
      keySpot = _this.currencySpecs[_this[_this.fieldA][0]].currency_name;
    } else if (_this.valuationCurrencySpecs) {
      keySpot =
        _this.valuationCurrencySpecs[_this.selectedCurrency].currency_name;
    }
    // Set up query parameters for retrieving data from Redis.
    const variablesRedis = {
      format: "json",
      key: `SPOT_${keySpot}`, // Construct the Redis key for spot currency data.
      observation_date: formatDate_YMDHMS(
        roundDateToFloor(_this.initCurrencyDate) // Round the date to floor for consistency.
      )
    };
    // Attempt to retrieve historical data from Redis.
    return getHistoricalData(_this, {
      params: paramsRedis,
      variables: variablesRedis,
      source: "getRedisData"
    }).catch(error => {
      console.error(error); // Log any errors if Redis retrieval fails.
      // Mark failure to retrieve historical data from Redis.
      Object.assign(_this, { faildGetHistoricalData: true });
      // Fallback to an API request to fetch spot currency data.
      const params = {
        method: "GET",
        endpoint: "currencies-intraday-history",
        content_type: "application/json",
        query_params: {
          format: "json",
          last: 1, // Get only the latest record.
          currency: pk == null ? _this.currencyNumber : pk // Use provided currency key or fallback.
        }
      };
      return apiService.getAll(params);
    });
  } else {
    console.error("Not using Redis, Warning!"); // Log a warning when Redis is not being used.
    // Mark failure to retrieve historical data from Redis.
    Object.assign(_this, { faildGetHistoricalData: true });
    // API request to fetch spot currency data when Redis is not used.
    const params = {
      method: "GET",
      endpoint: "currencies-intraday-history",
      content_type: "application/json",
      query_params: {
        format: "json",
        last: 1, // Fetch the most recent record.
        currency: pk == null ? _this.currencyNumber : pk // Use provided currency key or fallback.
      }
    };
    return apiService.getAll(params);
  }
}
